import type { ComboboxItemWithValue } from "@replo/design-system/components/combobox/Combobox";
import type { ImageModifierValue } from "schemas/generated/fileModifiers";

import React from "react";

import AspectRatioIcon from "@svg/modifiers/aspect-ratio";

import { useSemanticModifierValue } from "@agent-ui/features/modifiers/hooks/useSemanticModifierValue";
import {
  ComboboxContent,
  ComboboxItem,
  ComboboxList,
  ComboboxRoot,
  ComboboxTriggerButton,
} from "@replo/design-system/components/combobox/Combobox";
import { useOverridableState } from "replo-runtime/shared/hooks/useOverridableState";
import {
  ASPECT_RATIO_OPTIONS,
  extractAspectRatioFromClassName,
} from "replo-utils/lib/aspectRatio";

type AspectRatioModifierValue = Extract<
  ImageModifierValue,
  { type: "aspectRatio" }
>;

interface AspectRatioModifierProps {
  onChange: (modifier: AspectRatioModifierValue) => void;
}

const VALID_ASPECT_RATIO_VALUES: Set<string> = new Set(
  ASPECT_RATIO_OPTIONS.map((option) => option.value),
);

const AspectRatioModifier: React.FC<AspectRatioModifierProps> = ({
  onChange,
}) => {
  const aspectRatioFromElement =
    useSemanticModifierValue({
      semanticExtractor: ({ semanticElement }) => {
        return extractAspectRatioFromClassName(
          semanticElement.cssClasses || undefined,
        );
      },
    }) ?? "auto";

  const [aspectRatio, setAspectRatio] = useOverridableState(
    aspectRatioFromElement,
    React.useCallback(
      (value: AspectRatioModifierValue["value"]) => {
        onChange({ type: "aspectRatio", value });
      },
      [onChange],
    ),
  );

  const handleAspectRatioChange = React.useCallback(
    (value: string | null) => {
      if (value && VALID_ASPECT_RATIO_VALUES.has(value)) {
        setAspectRatio(value as AspectRatioModifierValue["value"]);
      }
    },
    [setAspectRatio],
  );

  const items: ComboboxItemWithValue[] = ASPECT_RATIO_OPTIONS.map((option) => ({
    value: option.value,
    label: option.label,
  }));

  const selectedItem = items.find((item) => item.value === aspectRatio) ?? null;

  return (
    <div className="aspect-ratio-modifier">
      <ComboboxRoot
        items={items}
        value={selectedItem}
        onValueChange={(item) => {
          handleAspectRatioChange(item?.value ?? null);
        }}
        itemToStringValue={(item) => String(item.label)}
        isItemEqualToValue={(a, b) => a.value === b.value}
        filter={null}
      >
        <ComboboxTriggerButton
          className="w-full"
          startEnhancer={<AspectRatioIcon className="w-4 h-4 text-muted" />}
        >
          {selectedItem?.label ?? "Custom"}
        </ComboboxTriggerButton>
        <ComboboxContent
          sideOffset={8}
          side="right"
          align="center"
          UNSAFE_className="w-[160px]"
        >
          <ComboboxList>
            {(item: ComboboxItemWithValue) => (
              <ComboboxItem key={item.value} {...item} value={item} />
            )}
          </ComboboxList>
        </ComboboxContent>
      </ComboboxRoot>
    </div>
  );
};

export default AspectRatioModifier;
